/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getEstimateData, getEstimateDataAction, getEstimateVersionsLatest } from "@Actions/EstimateList";
import { showToastNotification } from "@Reducers/toastSlice";
import {
  EstimateConfigration,
  EstimateCostDatabaseItemsGridNew,
  EstimateProperty,
  FilesTab as Files,
  ItemsGrid,
  SummaryTable,
} from "@Features/estimation";
import Measurements from "@Features/viewer-modals/components/Modal/Measurements";
import { deleteEstimate, isEstimateRequiredPermission, itemListSources, measurementSources } from "@Actions/estimation";
import TakeoffsList from "@Features/estimation/components/Takeoffs/TakeoffsList";
import { useNavigate, useParams } from "react-router-dom";
import IconComponent from "@CubeComponents/icons";
import SelectDropDown, { type SelectEventArgs } from "@CubeComponents/Mui_Form/SelectDropDown";
import {
  getEstimateUrl,
  getProjectDashboardPageUrl,
  getProjectEstimatesPageUrl,
} from "@Features/projects/utils/projectUrls";
import { PLATFORM, SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR } from "@Services/constants";
import { setBreadCrumbData } from "@Reducers/breadcrumbsdata";
import { setShowStickySidebar, setSidebarSelectedCell } from "@Reducers/stickySidebar";
import axiosWrapper from "@Services/axiosWrapper";
import { recentAccessConstant } from "@Components/RecentAccess/recentAccessConstant";
import type { RecentAccessResponse } from "@ServerTypes/recentAccess/recentAccess.schema";
import { convertDate } from "@Services/luxonHelper";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { folderPermissions } from "@Helpers/constants";
import _ from "lodash";
import EstimateNotFoundPage from "./EstimateNotFound";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import Text from "@CubeComponents/html/Text/Text";
import ProjectModuleNoAccessIllustration from "@Components/ProjectModulesAccessIllustration";
import { PORTAL_NO_SEAT } from "helpers";
import useUserModuleAccess from "@Services/userModuleAccessBySheetHook";
import type { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import type { RootState } from "src/reducers";
import type { EstimateCurrency, EstimateData as EstimateDataState } from "@ServerTypes/estimates/estimates.schema";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";

const { RESOURCE_MODIFY } = folderPermissions();

type ProjectDataLite = {
  id?: string;
  description?: string;
};

/**
 * Server SSOT is `estimateCurrencySchema` (`id` is `objectIdString`); everything is optional here
 * because the state starts empty, before `getEstimateData` has resolved the estimate's currency.
 */
type CurrencyData = Partial<EstimateCurrency>;

function Estimate() {
  const { t } = useTranslation();
  // Route params are the external entry point for these ids; the backend route schema validates them.
  const params = useParams<{ estimateId: ObjectIdString; versionId: ObjectIdString; projectId: ObjectIdString }>();
  const navigate = useNavigate();
  const projectData = useSelector((state: RootState) => state.projectData as ProjectDataLite);
  const estimateData = useSelector(
    (state: RootState) =>
      state.estimateData as EstimateDataState & {
        versionNumber?: string | number;
        description?: string;
        createdDate?: number;
      },
  );
  const estimateError = useSelector((state: RootState) => state.estimateError as boolean | number);
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();
  const moduleAccess = useUserModuleAccess();

  const [currentTab, setCurrentTab] = useState("BOQ");
  const [currencyData, setCurrencyData] = useState<CurrencyData>({});
  const [propertyEst, setPropertyEst] = useState(false);
  const [stateToUpdateBoqDatasource, setStateToUpdateBoqDatasource] = useState(true);
  const [allVersionsLatest, setAllVersionsLatest] = useState<Array<{ _id: string; text: string }>>([]);

  function updateStateToRefreshDatasource() {
    setStateToUpdateBoqDatasource((prev) => !prev);
  }

  function makeEstimateModalInvisible() {
    setPropertyEst((prevState) => !prevState);
  }

  const init = useCallback(async () => {
    if (params.estimateId) {
      dispatch(
        getEstimateData({
          estimateId: params.estimateId,
          versionId: params.versionId,
        }),
      );
    }
    dispatch(setSidebarSelectedCell("Estimates"));
    dispatch(setShowStickySidebar(true));
  }, [params.estimateId, params.versionId]);

  const fetchEstimateVersionsLatest = useCallback(async () => {
    const response = await getEstimateVersionsLatest({
      estimateId: params.estimateId!,
    });
    if (response) {
      const items = response.map(({ versionNumber, _id }: { versionNumber: string; _id: string }) => ({
        _id,
        text: versionNumber,
      }));
      setAllVersionsLatest(items);
    }
  }, [params.estimateId]);

  // `undefined` until the currency has loaded — the same condition the render guards below already
  // tested as `currencyData?.id`, now expressed in a form that carries the `objectIdString` brand
  // through to the child props instead of substituting an unbranded `""` sentinel.
  const costDatabase: object = estimateData.costDatabase ?? {};
  const costDatabaseVersionId =
    "costDatabaseVersionId" in costDatabase
      ? objectIdString.safeParse(costDatabase.costDatabaseVersionId).data
      : undefined;
  const safeCurrencyData: EstimateCurrency | undefined = currencyData.id
    ? { id: currencyData.id, selectedSymbol: { name: currencyData.selectedSymbol?.name ?? "" } }
    : undefined;

  useEffect(() => {
    init();
    return () => {
      dispatch(getEstimateDataAction({}));
    };
  }, [init]);

  useEffect(() => {
    if (estimateData._id) {
      document.title = `Estimate - ${estimateData.name}`;
    }
  }, [estimateData._id, estimateData.name]);

  useEffect(() => {
    if (estimateData._id && estimateData.estimateType === "merge") {
      setCurrentTab("Items");
    } else {
      setCurrentTab("BOQ");
    }
  }, [estimateData._id, estimateData.estimateType]);

  useEffect(() => {
    if (estimateData._id) {
      axiosWrapper.post<RecentAccessResponse>(`${getBackendUrl()}/recentAccess/recentAccess`, {
        module: recentAccessConstant.estimate,
        metaData: {
          _id: estimateData._id,
          name: estimateData.name,
          projectId: estimateData.project!._id,
          spaceId: estimateData.space!._id,
        },
        projectId: estimateData.project!._id,
        platform: PLATFORM.web,
      });
    }
  }, [estimateData._id]);

  const formatDate = (data: { createdDate?: number }) => (
    <div style={{ width: "fit-content" }}>
      <ToolTipComponent
        showTipPointer
        position="bottom"
        content={convertDate(Number(data.createdDate), "dateForTooltip")}
      >
        <span>{convertDate(Number(data.createdDate), "dateForText")}</span>
      </ToolTipComponent>
    </div>
  );

  useEffect(() => {
    if (projectData.id && estimateData.name) {
      // to do : uncomment once we start using slickGrid
      // breadCrumb.at(-1).link = getEstimatePageUrl(projectData.id, estimateData._id)
      dispatch(
        setBreadCrumbData([
          { text: t("Projects"), link: "/projects" },
          {
            text: projectData.description,
            link: getProjectDashboardPageUrl(projectData.id),
            showTooltip: true,
            tooltipData: projectData.description,
          },
          { text: t("Estimates"), link: getProjectEstimatesPageUrl(projectData.id) },
          {
            text: estimateData.name,
            showTooltip: true,
            tooltipData: estimateData.name,
          },
          ...(params.versionId ? [{ template: () => formatDate({ createdDate: estimateData.createdDate }) }] : []),
        ]),
      );
    }
  }, [projectData, estimateData, params.versionId]);

  useEffect(() => {
    if (estimateData._id) {
      if (estimateData.estimateType == "merge") {
        setCurrencyData(estimateData.costDatabaseArray?.[0]?.currency ?? {});
      } else {
        setCurrencyData(estimateData.costDatabase?.currency ?? {});
      }
      if (!params.versionId) {
        fetchEstimateVersionsLatest();
      }
    }
  }, [estimateData, fetchEstimateVersionsLatest, params.versionId]);

  const estimateDropdownButtonsItems = [
    {
      text: t("Delete"),
      id: "delete",
      disabled: !isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id),
    },
    { text: t("Properties"), id: "properties" },
  ];

  const selectActionFromMenu = useCallback(
    (args: SelectEventArgs) => {
      // `item.properties` is the untyped menu-item bag (index signature), so the `_id` this page
      // put on `allVersionsLatest` comes back as `unknown`; check it rather than assert it. A
      // non-string leaves `versionId` undefined, which `getEstimateUrl` already drops.
      const { _id } = args.item.properties;
      const popupUrl = getEstimateUrl({
        projectId: params.projectId,
        estimateId: params.estimateId,
        versionId: typeof _id === "string" ? _id : undefined,
      });
      window.open(popupUrl, "_blank");
    },
    [params.projectId, params.estimateId],
  );

  const handleBackClick = () => {
    const url = getEstimateUrl({
      projectId: params.projectId,
      estimateList: true,
    });
    navigate(url);
  };

  const handleDropDownSelectionHandler = async (args: SelectEventArgs) => {
    if (args.item.id == "delete" && params.estimateId) {
      const response = await deleteEstimate({ estimateId: params.estimateId });
      if (response.data.status) {
        handleBackClick();
      } else {
        dispatch(
          showToastNotification({
            type: TOAST_TYPE_ERROR,
            content: SOMETHING_WENT_WRONG,
          }),
        );
      }
    } else {
      makeEstimateModalInvisible();
    }
  };

  const versionsList = useCallback(
    () => (
      <SelectDropDown
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        items={allVersionsLatest}
        select={selectActionFromMenu}
        allowButtonPadding={false}
        cssClass="me-4"
        data-testid="versionListDropdown"
        maxHeight="15.625rem"
        valueTemplate={() => (estimateData.versionNumber ? `v${estimateData.versionNumber}` : t("Draft"))}
      />
    ),
    [allVersionsLatest, estimateData.versionNumber, selectActionFromMenu],
  );

  const additionalProperties = () => (
    <SelectDropDown
      select={handleDropDownSelectionHandler}
      items={estimateDropdownButtonsItems}
      allowButtonPadding={false}
      bgColor="bg-surface_neutral"
      cssClass="e-caret-hide p-0 border-none me-1"
      data-testid="threedotsBtn"
    >
      <IconComponent name="threedots" width="1.5rem" height="1.5rem" />
    </SelectDropDown>
  );

  const estimateHeadingInfo = () => (
    <div className="flex justify-between items-center bg-surface_neutral pb-2">
      <div className="flex items-center gap-4" data-testid="estimateHeadingLeft">
        <IconComponent
          className="cursor-pointer"
          name="BackwardArrow"
          onClick={handleBackClick}
          width="1.5rem"
          height="1.5rem"
        />
        <div className="flex flex-col gap-2" data-testid="estimateHeadingSection">
          <div className="flex items-center justify-start">
            <p className="me-2 text-2xl font-semibold text-[#192048]" data-testid="estimateTitle">
              {estimateData.name}
            </p>
          </div>
          {estimateData.description && (
            <p className="text-sm text-primary_text" data-testid="estimateDescription">
              {estimateData.description}
            </p>
          )}
        </div>
      </div>
      {!params.versionId && (
        <div className="flex items-center" data-testid="versionListSection">
          {versionsList()}
          {additionalProperties()}
        </div>
      )}
    </div>
  );

  function TabComponentStandard() {
    return (
      <div className="flex justify-between items-center border-0 border-y border-button_light">
        <div className="flex w-full justify-center">
          <div className="ms-6 cursor-pointer flex flex-col items-center">
            <ButtonComponent
              buttonType={BUTTON_TYPES.TEXT_BUTTON}
              className="pt-2 ps-1 pe-1 pb-1"
              onClick={() => setCurrentTab("BOQ")}
            >
              <Text>{t("BOQ")}</Text>
            </ButtonComponent>
            {currentTab === "BOQ" && (
              <div className="w-[calc(100%-0.5rem)] rounded-md border-4 border-[var(--gradient-color)]" />
            )}
          </div>
          <div className="ms-6 cursor-pointer flex flex-col items-center">
            <ButtonComponent
              buttonType={BUTTON_TYPES.TEXT_BUTTON}
              className="pt-2 ps-1 pe-1 pb-1"
              onClick={() => setCurrentTab("Takeoffs")}
            >
              <Text>{t("Takeoffs")}</Text>
            </ButtonComponent>
            {currentTab === "Takeoffs" && (
              <div className="w-[calc(100%-0.5rem)] rounded-md border-4 border-[var(--gradient-color)]" />
            )}
          </div>
          <div className="ms-6 cursor-pointer flex flex-col items-center">
            <ButtonComponent
              buttonType={BUTTON_TYPES.TEXT_BUTTON}
              className="pt-2 ps-1 pe-1 pb-1"
              onClick={() => setCurrentTab("Measurements")}
            >
              <Text>{t("Measurements")}</Text>
            </ButtonComponent>
            {currentTab === "Measurements" && (
              <div className="w-[calc(100%-0.5rem)] rounded-md border-4 border-[var(--gradient-color)]" />
            )}
          </div>
          <div className="ms-6 my-4 w-px bg-black" />
          <div className="ms-6 cursor-pointer flex flex-col items-center">
            <ButtonComponent
              buttonType={BUTTON_TYPES.TEXT_BUTTON}
              className="pt-2 ps-1 pe-1 pb-1"
              onClick={() => setCurrentTab("Files")}
            >
              <Text>{t("Files")}</Text>
            </ButtonComponent>
            {currentTab === "Files" && (
              <div className="w-[calc(100%-0.5rem)] rounded-md border-4 border-[var(--gradient-color)]" />
            )}
          </div>
          <div className="flex w-full justify-between">
            <div className="ms-6 cursor-pointer flex flex-col items-center">
              <ButtonComponent
                buttonType={BUTTON_TYPES.TEXT_BUTTON}
                className="pt-2 ps-1 pe-1 pb-1"
                onClick={() => setCurrentTab("Summary")}
              >
                <Text>{t("Summary")}</Text>
              </ButtonComponent>
              {currentTab === "Summary" && (
                <div className="w-[calc(100%-0.5rem)] rounded-md border-4 border-[var(--gradient-color)]" />
              )}
            </div>
            <div className="flex flex-col cursor-pointer">
              <ButtonComponent
                buttonType={BUTTON_TYPES.TEXT_BUTTON}
                className="flex gap-2 pe-8 pt-2 pb-1 items-center"
                onClick={() => setCurrentTab("CONFIGRATION")}
              >
                <div className="flex gap-2">
                  <IconComponent name="Slider" width="1.25rem" height="1.25rem" />
                  <span>
                    <Text>{t("Configuration")}</Text>
                  </span>
                </div>
              </ButtonComponent>
              {currentTab === "CONFIGRATION" && (
                <div className="w-[calc(100%-0.5rem)] rounded-md border-4 border-[var(--gradient-color)]" />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function TabComponentMerge() {
    return (
      <div className="flex items-center justify-start border-y border-[#ecf3fe]">
        <div className="flex justify-center">
          <div className="ms-6 cursor-pointer flex flex-col items-center">
            <ButtonComponent
              buttonType={BUTTON_TYPES.TEXT_BUTTON}
              className="pt-2 ps-1 pe-1 pb-1"
              onClick={() => setCurrentTab("Items")}
            >
              <Text>{t("Items")}</Text>
            </ButtonComponent>
            {currentTab === "Items" && (
              <div className="w-[calc(100%-0.5rem)] rounded-md border-4 border-[var(--gradient-color)]" />
            )}
          </div>
          <div className="ms-6 cursor-pointer flex flex-col items-center">
            <ButtonComponent
              buttonType={BUTTON_TYPES.TEXT_BUTTON}
              className="pt-2 ps-1 pe-1 pb-1"
              onClick={() => setCurrentTab("Summary")}
            >
              <Text>{t("Summary")}</Text>
            </ButtonComponent>
            {currentTab === "Summary" && (
              <div className="w-[calc(100%-0.5rem)] rounded-md border-4 border-[var(--gradient-color)]" />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full overflow-auto pe-6">
      {moduleAccess.ready &&
        (moduleAccess.portalSeat == PORTAL_NO_SEAT ? (
          <ProjectModuleNoAccessIllustration />
        ) : (
          <>
            {estimateError ? (
              <EstimateNotFoundPage projectId={params.projectId!} />
            ) : (
              estimateData._id === params.estimateId && (
                <div
                  style={{ background: "var(--surface-white)" }}
                  className="flex flex-col h-full w-full overflow-x-auto"
                >
                  {estimateData._id && estimateHeadingInfo()}
                  {estimateData._id && estimateData.estimateType == "merge"
                    ? TabComponentMerge()
                    : TabComponentStandard()}
                  {currentTab === "BOQ" && safeCurrencyData && _.get(estimateData, "_id") && (
                    <EstimateCostDatabaseItemsGridNew
                      estimateId={params.estimateId}
                      estimateVersionId={params.versionId || undefined}
                      isStaticData={true}
                      stateToUpdateBoqDatasource={stateToUpdateBoqDatasource}
                      currencyData={safeCurrencyData}
                      source={itemListSources.ESTIMATE}
                    />
                  )}
                  {currentTab === "Files" && <Files estimateId={params.estimateId} />}
                  {currentTab === "Measurements" && (
                    <Measurements estimateId={params.estimateId ?? ""} source={measurementSources.ESTIMATE} />
                  )}
                  {currentTab === "Takeoffs" && <TakeoffsList estimateId={params.estimateId} source="estimate" />}
                  {currentTab === "Items" && safeCurrencyData && estimateData._id && (
                    <ItemsGrid
                      currencyData={safeCurrencyData}
                      estimateId={estimateData._id}
                      costDatabaseVersionId={costDatabaseVersionId}
                      constituentEstimateVersionDetails={estimateData.constituentEstimateVersionDetails}
                    />
                  )}
                  {currentTab === "Summary" && safeCurrencyData && estimateData._id && (
                    <SummaryTable
                      currencyData={safeCurrencyData}
                      estimateId={estimateData._id}
                      versionId={params.versionId}
                    />
                  )}
                  {currentTab === "CONFIGRATION" && safeCurrencyData && (
                    <EstimateConfigration currencyData={safeCurrencyData} />
                  )}
                </div>
              )
            )}
            <div className="flex">
              {estimateData._id && propertyEst && !params.versionId && (
                <EstimateProperty
                  source="estimate"
                  updateStateToRefreshDatasource={updateStateToRefreshDatasource}
                  needPageRefresh={true}
                  estimateprop={{ _id: estimateData._id }}
                  makeModalInvisible={makeEstimateModalInvisible}
                />
              )}
            </div>
          </>
        ))}
    </div>
  );
}

export default Estimate;
